import {
  BaseSubscriptionArgs,
  addUserExtras,
  createPaymentMethodFromFormV2
} from '@dtx-company/app/shared/common/src/components/PaymentModal/PaymentModal.utils'
import { ClientSideError, ClientSideErrorType, cloneErrorObject, logger } from '@dtx-company/logger'
import {
  ConversionType,
  getConversionType
} from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/getConversionType'
import { PPESummarySection } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/PPESummarySection/PPESummarySection'
import { PaymentMethod } from '@stripe/stripe-js'
import { PaymentMethodPlanPurchaseFormField } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/PaymentMethodPlanPurchaseFormField'
import {
  Plan,
  PlanExtraDetail,
  PlanPurchaseExperienceMode
} from '@dtx-company/true-common/src/types/Plan'
import { PlanCostSummary } from '@dtx-company/inter-app/src/types/PlanCostSummary'
import {
  PlanExtras,
  usePlanExtrasForm
} from '@dtx-company/app/shared/common/src/components/ProFlex/AddOns'
import { PlanPicker } from '@dtx-company/app/shared/common/src/components/PlanPicker'
import { PlanPurchaseExperienceContext } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/PlanPurchaseExperienceContext'
import { PlanPurchaseExperienceHeading } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/PlanPurchaseExperienceHeading'
import { refreshStripePaymentElementForPaymentTypes } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/constants'
import { PlanPurchaseExperienceOrgNameInput } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/PlanPurchaseExperienceOrgNameInput'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PromoCode } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/PromoCode'
import { SubHeadingLink } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/SubHeadingLink'
import { SuccessfulPlanPurchaseCheckout } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/types'
import { UpgradeSuccessModal } from '@dtx-company/app/shared/common/src/components/UpgradeSuccessModal'
import { buySubscriptionV3 } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/buySubscriptionV3'
import { changeExistingPaymentMethod } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/changeExistingPaymentMethod'
import { downloadFlowcode } from '@dtx-company/app/shared/code/src/components/Homepage/CreateFlowcodeForm/CreateFlowcodeForm.utils'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getConversionSource } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/getConversionSource'
import { getPaymentAmount } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/GetPaymentAmount'
import { isOrgPlan } from '../../utils/isOrgPlan'
import { promoAppliesToSelectedPlan } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/promoAppliesToSelectedPlan'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useContext, useEffect, useState } from 'react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { useHasPlanDowngradeError } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/hooks/useHasPlanDowngradeError'
import { usePaymentMethodError } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/usePaymentMethodError'
import { usePlanCostSummarySWR } from '@dtx-company/app/shared/common/src/hooks/usePlanCostSummarySWR'
import { usePlanPurchaseExperienceExtraComposites } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceExtraComposites'
import { useRefreshTokenAfterSubscriptionChange } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/useRefreshTokenAfterSubscriptionChange'
import events from '@dtx-company/inter-app/src/event-tracking/events/proConversion'
import { Box, Divider, Stack, Typography } from '@dtx-company/design-system/src'

export interface PaymentStepProps {
  activeSubscriptionId?: string
  selectedPlan: Plan
  currentPlan?: Plan
  currentPlanExtraDetails?: PlanExtraDetail[]
  onSelectedPlanChange: (value: Plan) => void
  existingPaymentMethod?: PaymentMethod
  onSuccessfulCheckout?: (data: SuccessfulPlanPurchaseCheckout) => void
  showLearnMore?: boolean
  showChangeAddons: boolean
  mode?: PlanPurchaseExperienceMode
  planCostSummary: PlanCostSummary | undefined
  setPlanCostSummary: (value: PlanCostSummary | undefined) => void
}

export const fireCheckoutButtonAnalytic = (
  success: boolean,
  paymentMethodType?: string,
  existingpaymentMethod?: PaymentMethod,
  error?: string
): void => {
  let type
  if (paymentMethodType) {
    type = paymentMethodType
  } else if (existingpaymentMethod) {
    type =
      existingpaymentMethod.card && existingpaymentMethod.card.wallet
        ? existingpaymentMethod.card.wallet.type
        : existingpaymentMethod.type
  } else {
    type = 'card'
  }

  fireAnalyticsEvent('Revenue_Upgrade_Clicked_CheckoutButton', {
    success: success,
    paymentType: type,
    existingPaymentMethod: !!existingpaymentMethod,
    error
  })
}

export function PaymentStep(props: PaymentStepProps): JSX.Element {
  const context = useContext(PlanPurchaseExperienceContext)
  const { authState, plans } = context
  const flowRouter = useFlowRouter()

  // Long-term, this can replace alot of other functionality cause it
  // combines the currentQuantity into the full model to simplify look-ups.
  // But short-term, it was requiring rewriting too much to implement it
  // so it can be used as a simple PlanExtra but providing
  // a consistent sort order
  const planExtras = usePlanPurchaseExperienceExtraComposites(
    props.selectedPlan.planExtras,
    props.currentPlanExtraDetails
  )

  const stripe = useStripe()
  const [showExistingPaymentMethod, setShowExistingPaymentMethod] = useState(true)
  const [usedPaymentMethod, setUsedPaymentMethod] = useState<PaymentMethod>()
  const [isUpgradeSuccessDialogOpen, setIsUpgradeSuccessDialogOpen] = useState(false)
  const [newPlan, setNewPlan] = useState<Plan | undefined>(undefined)
  const [oldPlan, setOldPlan] = useState<Plan | undefined>(undefined)
  const [paymentIsCompleted, setPaymentIsCompleted] = useState<boolean>(false)
  const [paymentMethodType, setPaymentMethodType] = useState<string | undefined>(undefined)

  const { name: cohortName } = useCohort()

  const isOrg = isOrgPlan(props.selectedPlan.type)
  const isProFlex = props.selectedPlan.type === PlanType.PRO_FLEX
  // Need to track isSubmitting separately until we make the form submit uniform
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    formValues: planExtraFormValues,
    planExtraHeader,
    planExtraRender,
    showContactSalesCard,
    handlePlanExtras,
    handleBusinessName,
    errors,
    form: extrasForm
  } = usePlanExtrasForm({
    planExtras,
    currentPlanExtraDetails: props.currentPlanExtraDetails,
    availablePlanExtras: context.availablePlanExtrasForSelectedPlan
  })
  const isExtrasFormValid =
    props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT || !Object.values(errors || {}).length
  const isSummaryValid = props.currentPlanExtraDetails ? isExtrasFormValid : true

  // TODO: optimize SWR and minimize dependencies. Move it to a parent component.
  //  planCostSummarySWR  depends on isSummaryValid and isExtrasFormValid -> usePlanExtrasForm (the whole form)
  const planCostSummarySWR = usePlanCostSummarySWR(
    () => {
      if (!authState.token) return
      if (!isSummaryValid) return
      // NOTE: no longer passes payment method or address so that we
      // don't calculate tax anymore
      return {
        token: authState.token,
        newPlanId: props.selectedPlan.id,
        promoCode: context.allowPromoCode ? context.promoCodeState.promo?.id : undefined,
        planExtraDetails: Object.values(planExtraFormValues.planExtraDetails || {})
      }
    },
    {
      debounceInterval: 500,
      revalidateIfStale: false,
      revalidateOnFocus: false
    }
  )

  const { hasDowngradeError } = useHasPlanDowngradeError({
    error: planCostSummarySWR.error
  })
  const [mountPaymentElement, setMountPaymentElement] = useState(true)

  useEffect(() => {
    /* SWR fetches old cached values while revalidating, but if the query changes
          to something it hasn't seen yet then it returns undefined until it fetches it for
          the first time, which makes the app swap to its loading state back and forth
          as you enter the form - better to keep the old value displayed until the
          new value is ready */
    if (planCostSummarySWR.isValidating || !planCostSummarySWR.data) return

    elements?.update({
      amount: getPaymentAmount(planCostSummarySWR.data.grandTotalInCents)
    })

    props.setPlanCostSummary(planCostSummarySWR.data)
    if (
      paymentMethodType &&
      refreshStripePaymentElementForPaymentTypes.includes(paymentMethodType)
    ) {
      // stripe payment element has a bug related to amount update. If payment method google pay is selected
      // and amount is changed, confirmation google modal window will be presented with an old value. If amount is changed
      // and then the payment method is changed to google pay, the amount displays as expected.
      // Remove this code when the Stripe bug is fixed
      setMountPaymentElement(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCostSummarySWR.isValidating, planCostSummarySWR.data, paymentMethodType])

  useEffect(() => {
    if (!mountPaymentElement) setMountPaymentElement(true)
  }, [mountPaymentElement])

  useEffect(() => {
    // Should only need to be set as an initial value, but also if
    // the permission is somehow removed or card is lost then can
    // handle that too
    if (!props.existingPaymentMethod || props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) {
      setShowExistingPaymentMethod(false)
    } else {
      setShowExistingPaymentMethod(true)
    }
  }, [props.existingPaymentMethod, props.mode])

  const {
    fileTypeToDownload: downloadFileType,
    downloadCodeData: { codeOptions, codeBatchId }
  } = useCodeSelector(state => state.homePage)

  const { jwt } = useAuthState()
  const { refreshTokenAfterSubscriptionChange } = useRefreshTokenAfterSubscriptionChange()

  const elements = useElements()

  async function checkFormValidation({ selectedPlan }: { selectedPlan: Plan }): Promise<boolean> {
    // TODO: need to combine the extras form and payment form together
    // Until then, the form might not have errors if the field wasn't touched
    // so need to run a manual validation and abort if any issues
    const isOrg = isOrgPlan(selectedPlan.type)
    const shouldValidateBusinessName =
      isOrg && props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT && !props.showChangeAddons
    if (shouldValidateBusinessName) {
      await extrasForm.trigger('businessName')
      if (!extrasForm.formState.isValid) {
        // another hack to bring the field into view cause they're likely scrolled to
        // the bottom and there's no flash notification or anything, the button just disables
        // on click
        // can't use extrasForm.setFocus('businessName') cause it throws an error that
        // the fieldRef isn't defined, might not be wired up properly (and even if not
        // for that, the field might be hidden under the top nav or step overlay)
        window.scrollTo(0, 0)
        return false
      }
    }
    return true
  }

  function getBusinessName({ selectedPlan }: { selectedPlan: Plan }): string | undefined {
    // BUGFIX: /subscriptions endpoint expects an orgName when upgrading
    // org plans like pro+org monthly -> pro+org yearly but these cases don't require
    // the user to enter a new name and don't show the field - validation will handle
    // stopping them from getting this far if the field is visible, this will
    // fallback to their current value so that the API can work.
    // (side note: v2 api will have a better way to indicate org vs personal plans)
    return isOrgPlan(selectedPlan.type)
      ? planExtraFormValues.businessName || authState.jwt?.org?.name
      : undefined
  }

  function getBaseSubscriptionArgs({
    token,
    currentPlan,
    selectedPlan,
    businessName,
    activeSubscriptionId
  }: {
    token: string
    currentPlan: Plan | undefined
    selectedPlan: Plan
    businessName: string | undefined
    activeSubscriptionId: string | undefined
  }): BaseSubscriptionArgs {
    const promoCodeIsValid =
      context.promoCodeState.promo &&
      promoAppliesToSelectedPlan(context.promoCodeState.promo, {
        id: selectedPlan.id
      })
    return {
      token: token,
      currentPlanId: currentPlan?.id,
      selectedPlanId: selectedPlan.id,
      promoCode:
        context.allowPromoCode && promoCodeIsValid ? context.promoCodeState.promo?.id : undefined,
      planExtraDetails: Object.values(planExtraFormValues.planExtraDetails || {}),
      subscriptionId: activeSubscriptionId,
      ...(businessName && { orgName: businessName })
    }
  }

  const { sendPaymentMethodErrorMessage } = usePaymentMethodError()

  async function handlePaymentFormValid(): Promise<void> {
    if (!authState.token) return

    // These can maybe change after the upgrade so getting a reference
    // to them now first to later be consistent after awaits
    const { selectedPlan, currentPlan } = props
    let updatedSubscriptionId: string | undefined

    // Technically the payment form is already valid, but a second form was added to
    // the page and we haven't had time to combine them yet so need some extra work
    // to ensure it's also valid
    const isValid = await checkFormValidation({ selectedPlan })
    if (!isValid) {
      return
    }

    // Like above, this is not in the `data` object because of the separate form object
    const businessName = getBusinessName({ selectedPlan })

    const baseSubscriptionArgs = getBaseSubscriptionArgs({
      token: authState.token,
      currentPlan,
      selectedPlan,
      businessName,
      activeSubscriptionId: props.activeSubscriptionId
    })

    try {
      if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT && elements && stripe) {
        const paymentMethod = await createPaymentMethodFromFormV2({
          elements,
          stripe
        })
        setUsedPaymentMethod(paymentMethod)
        if (!props.activeSubscriptionId) {
          logger.logError(
            new ClientSideError({
              name: 'change payment method',
              type: ClientSideErrorType.ClientSideValidationError,
              message: 'No active subscription'
            }),
            { technicalArea: 'monetization' }
          )

          throw new Error('No active subscription')
        }
        if (!paymentMethod) {
          logger.logError(
            new ClientSideError({
              name: 'change payment method',
              type: ClientSideErrorType.ClientSideValidationError,
              message: 'No payment method'
            }),
            { technicalArea: 'monetization' }
          )
          throw new Error('No payment method')
        }
        await changeExistingPaymentMethod({
          paymentMethodId: paymentMethod.id,
          paymentMethodType: paymentMethodType,
          subscriptionId: props.activeSubscriptionId,
          token: authState.token,
          stripe
        })
      } else {
        const paymentMethod = await (async (): Promise<PaymentMethod | undefined> => {
          if (paymentIsCompleted && stripe && elements) {
            return await createPaymentMethodFromFormV2({ elements, stripe })
          } else if (props.existingPaymentMethod) {
            return props.existingPaymentMethod
          } else {
            logger.logError(
              new ClientSideError({
                name: 'add payment method',
                type: ClientSideErrorType.ClientSideValidationError,
                message: 'No payment method'
              }),
              { technicalArea: 'monetization' }
            )
            throw new Error('No payment method')
          }
        })()

        setUsedPaymentMethod(paymentMethod)
        // When using the existing card, do not send the paymentMethod
        updatedSubscriptionId = await buySubscriptionV3({
          paymentMethodType: paymentMethodType,
          paymentMethodId: paymentMethod?.id,
          stripe: stripe,
          ...baseSubscriptionArgs
        })
      }
      fireCheckoutButtonAnalytic(true, paymentMethodType, props.existingPaymentMethod)

      if (props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT) {
        await addUserExtras(
          {
            query: flowRouter.query
          },
          jwt
        )
        events.userCompletedProConversion({
          planSelected: selectedPlan.id,
          previousPlan: currentPlan?.id,
          promoCode:
            context.promoCodeState.state === 'valid' ? context.promoCodeState.promo?.id : undefined,
          subscriptionId: updatedSubscriptionId,
          cohort: cohortName,
          email: authState.jwt?.email ?? '',
          ithacaId: authState.jwt?.ithacaId ?? '',
          planPrice: selectedPlan.priceInCents,
          conversionSource: getConversionSource(flowRouter),
          isLoggedIn: authState.isAuthChecked && authState.isAuthenticated,
          conversionType: getConversionType(currentPlan, selectedPlan, props.mode),
          planDetails: Object.values(planExtraFormValues.planExtraDetails || {}),
          paymentMethodType,
          couponDiscountInCents: props.planCostSummary?.couponDiscountInCents
        })
      }
      // Must refresh so that the plan in the header changes properly between FREE vs PRO vs PRO PLUS
      await refreshTokenAfterSubscriptionChange(updatedSubscriptionId || props.activeSubscriptionId)
      // Prompt to download code (if applicable)
      if (codeOptions && codeBatchId) {
        const flowcodeOptions = JSON.parse(codeOptions)
        await downloadFlowcode(downloadFileType, flowcodeOptions, codeBatchId)
      }
      // Redirect / success messaging / etc - must be last since it could redirect
      // and thus unload this component
      const conversionType = getConversionType(currentPlan, selectedPlan, props.mode)
      setNewPlan(selectedPlan)
      setOldPlan(currentPlan)
      if (
        conversionType == ConversionType.UPGRADE ||
        conversionType === ConversionType.ACTIVATE_TRIAL
      ) {
        setIsUpgradeSuccessDialogOpen(true)
      } else {
        await props.onSuccessfulCheckout?.({
          oldPlan: currentPlan,
          newPlan: selectedPlan,
          isSwitchingBillingCycle: context.isSwitchingBillingCycle
        })
      }
    } catch (error) {
      sendPaymentMethodErrorMessage(error)
      if (!error.type) {
        logger.logError(
          new ClientSideError({
            ...cloneErrorObject(error),
            type: ClientSideErrorType.DefaultError
          }),
          { technicalArea: 'monetization' }
        )
      }
      fireCheckoutButtonAnalytic(
        false,
        paymentMethodType,
        props.existingPaymentMethod,
        error?.message || error
      )
      await refreshTokenAfterSubscriptionChange(updatedSubscriptionId || props.activeSubscriptionId)
    }
  }

  const isFormValid = Boolean(
    isExtrasFormValid &&
      props.selectedPlan &&
      ((showExistingPaymentMethod && props.existingPaymentMethod) ||
        (!showExistingPaymentMethod && paymentIsCompleted))
  )

  const showPlanExtras = planExtras && planExtras.length > 0

  return (
    <>
      <Stack
        data-testid={'payment-step'}
        direction="row"
        justifyContent="center"
        sx={{
          flex: '1 0 auto',
          position: 'relative',
          zIndex: 0,
          px: 8.5,
          py: { xs: 4, lg: 0 }
        }}
      >
        <form
          method="post"
          action=""
          onSubmit={async event => {
            event.preventDefault()
            setIsSubmitting(true)
            try {
              await handlePaymentFormValid()
            } finally {
              setIsSubmitting(false)
            }
          }}
        >
          <PlanPurchaseExperienceHeading
            selectedPlan={props.selectedPlan}
            mode={props.mode}
            planCostSummaryType={props.planCostSummary?.type}
          />
          {props.showLearnMore && <SubHeadingLink selectedPlan={props.selectedPlan} />}
          {props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT && (
            <>
              {isOrg && !props.showChangeAddons && (
                <>
                  <PlanPurchaseExperienceOrgNameInput
                    heading={'Please create a name for your account'}
                    label={'This is the name others in your group will see when collaborating'}
                    value={planExtraFormValues.businessName}
                    onValueChange={handleBusinessName}
                    errorText={errors?.businessName?.message}
                  />
                  <Divider sx={{ my: 4 }} />
                </>
              )}
              <_Subheading isProFlex={isProFlex} showChangeAddons={props.showChangeAddons} />
              <Box sx={theme => ({ mt: theme.spacing(4), maxWidth: '100%' })}>
                {plans && (
                  <PlanPicker
                    currentPlan={props.currentPlan}
                    selectedPlan={props.selectedPlan}
                    plans={plans}
                    onSelectedPlanChange={props.onSelectedPlanChange}
                    promo={context.promoCodeState.promo || {}}
                  />
                )}
              </Box>
              {showPlanExtras && (
                <PlanExtras
                  planExtraHeader={planExtraHeader}
                  planExtraRender={planExtraRender}
                  showContactSalesCard={showContactSalesCard}
                  handlePlanExtras={handlePlanExtras}
                  selectedPlan={props.selectedPlan}
                />
              )}
              {context.allowPromoCode && (
                <Box
                  sx={{
                    mt: '1rem'
                  }}
                >
                  <PromoCode />
                </Box>
              )}
              <Divider sx={{ my: 4 }} />
            </>
          )}
          <Typography component="h2" variant="subtitleSmall">
            Payment
          </Typography>
          <Box sx={{ mt: '8px' }}>
            {mountPaymentElement && (
              <PaymentMethodPlanPurchaseFormField
                existingPaymentMethod={props.existingPaymentMethod}
                showExistingCreditCard={showExistingPaymentMethod}
                setPaymentIsCompleted={setPaymentIsCompleted}
                setPaymentType={setPaymentMethodType}
                onShowExistingCreditCardChange={value => {
                  setShowExistingPaymentMethod(value)
                }}
                paymentMethodType={paymentMethodType}
              />
            )}
          </Box>
          {props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT && <Divider sx={{ my: 4 }} />}
          <PPESummarySection
            selectedPlan={props.selectedPlan}
            currentPlan={props.currentPlan}
            promo={context.promoCodeState.promo || {}}
            isProcessing={isSubmitting}
            planCostSummary={props.planCostSummary}
            currentPlanExtraDetails={props.currentPlanExtraDetails}
            planExtraFormValues={planExtraFormValues}
            planExtraRender={planExtraRender}
            isFormValid={isFormValid}
            showChangeAddons={props.showChangeAddons}
            mode={props.mode}
            hasDowngradeError={hasDowngradeError}
          />
        </form>
      </Stack>
      <UpgradeSuccessModal
        open={isUpgradeSuccessDialogOpen}
        onClose={async () => {
          setIsUpgradeSuccessDialogOpen(false)
          await props.onSuccessfulCheckout?.({
            oldPlan: oldPlan,
            newPlan: newPlan,
            isSwitchingBillingCycle: context.isSwitchingBillingCycle
          })
        }}
        planType={props.selectedPlan.type}
        paymentMethod={usedPaymentMethod}
      />
    </>
  )
}

function _Subheading({
  isProFlex,
  showChangeAddons
}: {
  isProFlex: boolean
  showChangeAddons?: boolean
}): JSX.Element {
  let subHeading = isProFlex ? 'Plan Details' : 'Select plan'
  if (showChangeAddons) {
    subHeading = 'Current plan details'
  }
  return <Typography variant="subtitleSmall">{subHeading}</Typography>
}
